import { type Boundary, type LatLngTuple, isValidCoordinate } from 'utils/latLng';

const parseBoundaryRing = (value: string): Boundary | null => {
  const ring: Boundary = [];
  for (const pair of value.split(',')) {
    const coordinates = pair.trim().split(/\s+/);
    if (coordinates.length !== 2) {
      return null;
    }
    const [lng, lat] = coordinates.map(Number);
    if (!isValidCoordinate(lat) || !isValidCoordinate(lng)) {
      return null;
    }
    ring.push([lat, lng]);
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  const distinctPoints = new Set(ring.map(([lat, lng]) => `${lat},${lng}`));
  return ring.length >= 4 &&
    first[0] === last[0] &&
    first[1] === last[1] &&
    distinctPoints.size >= 3
    ? ring
    : null;
};

// The Site outline follows each polygon's exterior ring; interior rings are holes.
const parseOuterRing = (value: string): Boundary | null =>
  parseBoundaryRing(value.split(/\)\s*,\s*\(/)[0]);

export const parseBoundaryPolygons = (value?: string | null): Boundary[] => {
  if (!value) {
    return [];
  }
  const boundary = value.trim();

  const polygonMatch = boundary.match(/^POLYGON\s*\(\((.+)\)\)$/i);
  if (polygonMatch) {
    const ring = parseOuterRing(polygonMatch[1]);
    return ring ? [ring] : [];
  }

  const multipolygonMatch = boundary.match(/^MULTIPOLYGON\s*\(\(\((.+)\)\)\)$/i);
  if (!multipolygonMatch) {
    return [];
  }

  return multipolygonMatch[1]
    .split(/\)\)\s*,\s*\(\(/)
    .map(parseOuterRing)
    .filter((ring): ring is Boundary => ring !== null);
};

export const parseBoundary = (value?: string | null): LatLngTuple[] =>
  parseBoundaryPolygons(value)[0] ?? [];

type GeoBoundaryFieldValue = {
  fieldDefinition?: { name?: string | null } | null;
  textValue?: string | null;
} | null;

export const getGeoBoundaryPolygons = (
  fieldValues?: ReadonlyArray<GeoBoundaryFieldValue> | null
): Boundary[] =>
  parseBoundaryPolygons(
    fieldValues?.find((value) => value?.fieldDefinition?.name?.toLowerCase() === 'geo boundary')
      ?.textValue
  );

export const getCentroid = (coords: LatLngTuple[]): LatLngTuple | null => {
  if (!coords.length) {
    return null;
  }
  const [latSum, lngSum] = coords.reduce((acc, [lat, lng]) => [acc[0] + lat, acc[1] + lng], [0, 0]);
  return [latSum / coords.length, lngSum / coords.length];
};
